import {
  AxialMovementPosition,
  CustomPreviewProps,
  KilnInfo,
  MetricSignalMapping,
  SignalAnomaly,
  StationParts
} from '@domain/kiln-monitoring'
import {Stack} from '@mui/material'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {DrivePart, KilnShellPart, MaterialFlow, Rotation, StationPart} from './kiln-parts'

import './kilnPlaceholder.css'

interface KilnSvgWrapperProps {
  svgClickCb?: (localNames: string[]) => void
  sensorMappings: MetricSignalMapping[]
  anomalies?: SignalAnomaly[]
  activeAreas?: string[]
  hoveredArea?: string
  speedValue?: number
  isWidget?: boolean
  kilnInfo: KilnInfo
  containerWidth?: number
  kilnLength?: number
  rollerMovementByPartId?: Record<string, AxialMovementPosition>
  customSvgPreviews?: {
    drive?: (props: CustomPreviewProps) => React.ReactNode
    station?: (props: CustomPreviewProps) => React.ReactNode
    kilnShell?: (props: CustomPreviewProps) => React.ReactNode
  }
}

export const KilnSvgWrapper = ({
  svgClickCb,
  sensorMappings,
  anomalies,
  activeAreas,
  hoveredArea,
  speedValue,
  isWidget,
  kilnInfo,
  rollerMovementByPartId,
  customSvgPreviews,
  containerWidth,
  kilnLength
}: KilnSvgWrapperProps) => {
  const {t} = useTranslation()

  const svgRef = useRef<null | SVGElement>(null)
  const warningIds = useMemo(
    () => anomalies?.filter((a) => a.status === 'Warning').map((a) => a.svgId) ?? [],
    [anomalies]
  )
  const errorIds = useMemo(
    () => anomalies?.filter((a) => a.status === 'Alarm').map((a) => a.svgId) ?? [],
    [anomalies]
  )
  const [prevActiveAreas, setPrevActiveAreas] = useState<string[]>([])
  const [prevWarnings, setPrevWarnings] = useState<string>('[]')
  const [prevErrors, setPrevErrors] = useState<string>('[]')

  const kilnWidthInPx = kilnInfo.layout.referenceDimensions.width
  const widthScaleFactor = containerWidth ? containerWidth / kilnWidthInPx : 1
  const scaledKilnWidth = kilnWidthInPx * widthScaleFactor
  const meterToPixel = kilnLength ? (kilnWidthInPx / kilnLength) * widthScaleFactor : undefined

  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    const target = e.nativeEvent.target as SVGElement
    const parent = target.parentElement as HTMLElement
    const clickedElement = target.id ? target : parent
    if (!clickedElement.id) return

    const sensors = sensorMappings.find((s) => s.svgId === clickedElement.id)
    if (!sensors) return

    e.stopPropagation()
    if (svgClickCb) svgClickCb(sensors.localNames)
  }

  const removeOverlays = (styleClass: string) => {
    const collection = document.getElementsByClassName(styleClass)
    Array.from(collection).forEach((element) => element.classList.remove(styleClass))
  }

  const addOverlay = useCallback((parent: SVGElement, elementIds: string[], styleClass: string) => {
    removeOverlays(styleClass)
    elementIds.map((elementId) => {
      const target = parent.querySelector('#' + elementId)
      if (target) {
        target.classList.add(styleClass)
      }
    })
  }, [])

  // Automatically add hover class to kiln areas with sensors connected
  useEffect(() => {
    const activeAreas: string[] = []
    sensorMappings.forEach((mapping) => {
      if (!activeAreas.includes(mapping.svgId)) {
        activeAreas.push(mapping.svgId)
      }
    })

    if (svgRef?.current) {
      const prevActive = svgRef.current?.getElementsByClassName('kiln-part')
      Array.from(prevActive).forEach((element) => element.classList.remove('kiln-part'))
    }

    activeAreas.map((area) => {
      if (svgRef?.current) {
        const parent = svgRef.current
        const target = parent.querySelector('#' + area)
        if (target) {
          target.classList.add('kiln-part')
        } else {
          console.warn('SVG area with id missing : ' + area)
        }
      }
    })
  }, [sensorMappings, svgRef])

  // Based on received warnings and errors display corresponding overlay over kiln area with problematic sensors
  useEffect(() => {
    const errorsStr = JSON.stringify(errorIds)
    const warningsStr = JSON.stringify(warningIds)
    if (svgRef?.current && (errorsStr !== prevErrors || warningsStr !== prevWarnings)) {
      setPrevWarnings(warningsStr)
      setPrevErrors(errorsStr)
      if (errorIds) addOverlay(svgRef.current, errorIds, 'error')
      if (warningIds) addOverlay(svgRef.current, warningIds, 'warning')
    }
  }, [svgRef, warningIds, errorIds, prevErrors, prevWarnings, addOverlay])

  useEffect(() => {
    if (svgRef?.current && activeAreas) {
      const parent = svgRef.current
      activeAreas.map((areaId) => {
        const target = parent.querySelector('#' + areaId)
        if (target) target.classList.add('kiln-part-active')
      })
      prevActiveAreas.map((prevAreaId) => {
        const stillActive = activeAreas.findIndex((areaId) => areaId === prevAreaId)
        if (stillActive === -1) {
          const target = parent.querySelector('#' + prevAreaId)
          if (target) target.classList.remove('kiln-part-active')
        }
      })

      setPrevActiveAreas(activeAreas)
    }
  }, [svgRef, activeAreas, prevActiveAreas])

  useEffect(() => {
    if (svgRef?.current) {
      const parent = svgRef.current

      const target = parent.querySelector('#' + hoveredArea)
      const activeHoverAreas = Array.from(parent?.getElementsByClassName('kiln-part-hover'))
      activeHoverAreas.map((area) => {
        area.classList.remove('kiln-part-hover')
      })
      if (target) target.classList.add('kiln-part-hover')
    }
  }, [svgRef, hoveredArea])

  return (
    <Stack alignItems="flex-start" sx={{maxHeight: '100%'}}>
      <svg
        ref={svgRef as React.LegacyRef<SVGSVGElement> | undefined}
        viewBox={`0 0 ${scaledKilnWidth + 140} ${kilnInfo.layout.referenceDimensions.height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        height="100%"
        // width="100%" // this will center the svg
        onClick={handleSvgClick}
        style={{
          maxHeight: '300px'
        }}
      >
        <Rotation
          x={0}
          y={kilnInfo.layout.drive.position === 'bottom' ? -25 : 0}
          duration={speedValue ? Math.round(60 / speedValue) : 0}
          isRotationClockwise={kilnInfo.layout.isRotationClockwise}
        />
        <svg x={140}>
          <KilnShellPart
            {...kilnInfo.layout.kilnShell}
            widthScaleFactor={widthScaleFactor}
            customSvgPreview={customSvgPreviews?.kilnShell}
            meterToPixel={meterToPixel}
          />

          {kilnInfo.layout.stations.map((station) => (
            <StationPart
              key={station.id}
              {...station}
              topRollerMovement={
                rollerMovementByPartId?.[`${station.id}_${StationParts.SUPPORT_ROLLER_TOP}`]
              }
              bottomRollerMovement={
                rollerMovementByPartId?.[`${station.id}_${StationParts.SUPPORT_ROLLER_BOTTOM}`]
              }
              customSvgPreview={customSvgPreviews?.station}
              widthScaleFactor={widthScaleFactor}
              meterToPixel={meterToPixel}
            />
          ))}
          <DrivePart
            {...kilnInfo.layout.drive}
            // the default "side" is left
            flip={
              kilnInfo.layout.drive.side === 'right' ||
              (!kilnInfo.layout.drive.side && kilnInfo.layout.materialFlow !== 'rightToLeft') // this is for backwards compatibility
            }
            customSvgPreview={customSvgPreviews?.drive}
            widthScaleFactor={widthScaleFactor}
            meterToPixel={meterToPixel}
          />
          <MaterialFlow
            materialFlow={kilnInfo.layout.materialFlow}
            x={550}
            y={kilnInfo.layout.kilnShell.y + kilnInfo.layout.kilnShell.height / 2 - 25}
            text={t('kilnConfig.materialFlow')}
            display={isWidget ? 'none' : 'block'}
          />
        </svg>
      </svg>
    </Stack>
  )
}
